// HTTP transport layer shared by every backend call.
//
// This module owns the parts of the client that are identical for every
// endpoint: the base URL, the CSRF token cache and its auto-recovery, the
// auth-bootstrap gate, the ApiError / ApiResponse shapes, and the fetch
// wrapper that ties them together. It was lifted out of `api.ts` when that
// file crossed the 800 LOC limit; the endpoint definitions stayed there.
//
// Belongs here: anything that applies to all requests — headers,
// credentials, retry/recovery, response parsing, transport-level types.
// Does not belong here: per-endpoint paths and payloads. Those stay in
// `api.ts` (or a future per-domain module beside it) so this file remains
// the single place the request lifecycle is described.
//
// Credentials (auth migration, phase 5b-2)
// ----------------------------------------
// The backend's `get_current_user` accepts two credentials on every route:
//
//   1. `Authorization: Bearer <Supabase access token>` — the app's
//      credential. The browser holds a Supabase session from login
//      (stores/authStore), supabase-js keeps it refreshed, and request()
//      reads it via `supabase.auth.getSession()` (an in-memory read, no
//      network) and attaches it to every request that has one.
//   2. The HttpOnly `access_token` cookie issued by /auth/login — the
//      fallback. Since 5b-2 it is acquired only by the legacy fallback login
//      (an account whose password Supabase rejects; see authStore.login) —
//      the direct login leaves the browser with the Supabase session alone.
//      Where it exists it rides along automatically via
//      `credentials: 'include'`, so a request with no Supabase session
//      (Supabase not configured, an account the sign-in rejected, session
//      storage unavailable) still authenticates the way it always has. The
//      CSRF header is attached regardless: the backend ignores it for Bearer
//      requests, and the cookie path still needs it.
//
// The auth-bootstrap paths (BOOTSTRAP_EXEMPT_PATHS) never carry the Bearer.
// /auth/login, /auth/login-v2, /auth/register, /auth/register-v2 and
// /auth/refresh exist to issue the cookie pair and are authenticated by the
// credentials in their body (or the refresh cookie), not by a session; and
// /auth/logout has to clear the cookie of the browser that sent it, which
// only the cookie path guarantees.
//
// Transition safety: production may still run a backend that does not
// understand the Supabase Bearer and answers it with 401. A 401 on a request
// that carried the Bearer is therefore retried once, cookie-only, before it
// surfaces to the caller. This retry is a bridge for the deploy window and
// goes away together with the cookie once it is retired; do not build on it.

import { supabase } from '../supabaseClient';
import logger from '../utils/logger';

// 環境変数からAPI URLを取得、なければローカル開発用のURLを使用
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8002/api';

/**
 * Helper to read a cookie value by name.
 * Used to read the CSRF token from the csrf_token cookie when frontend
 * and backend share an origin.
 */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// CSRF token cache. In cross-origin deployments (Vercel frontend ↔ Render
// backend) the JS on the frontend origin can't read the csrf_token cookie
// the backend sets on its own origin, so we capture the token from auth
// response bodies (login / refresh / register) and replay it as the
// X-CSRF-Token header. sessionStorage survives full reloads but not
// new tabs, which matches the lifetime of the auth cookie pair.
const CSRF_STORAGE_KEY = 'okuri.csrf_token';
let csrfTokenCache: string | null = readCsrfFromStorage();

function readCsrfFromStorage(): string | null {
  try {
    return window.sessionStorage.getItem(CSRF_STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistCsrfToken(token: string | null): void {
  try {
    if (token) {
      window.sessionStorage.setItem(CSRF_STORAGE_KEY, token);
    } else {
      window.sessionStorage.removeItem(CSRF_STORAGE_KEY);
    }
  } catch {
    /* sessionStorage may be unavailable (private mode, SSR) — ignore. */
  }
}

export function setCsrfToken(token: string | null): void {
  csrfTokenCache = token;
  persistCsrfToken(token);
}

function getCsrfToken(): string | null {
  // Prefer the cached token captured from the last auth response. Fall
  // back to document.cookie for same-origin deployments where the
  // csrf_token cookie is readable by JS.
  return csrfTokenCache ?? getCookie('csrf_token');
}

// Session bootstrap gate.
//
// Holds backend requests until an in-flight login has produced the cookie +
// CSRF token they would otherwise fail without (401 has no client-side
// recovery, and the 403 recovery below needs a refresh cookie that only
// /auth/login issues). Until 5b-2 the direct login set it while it fetched
// the legacy cookie in the background; it no longer acquires that cookie, and
// the legacy fallback awaits /auth/login itself, so nothing in the store sets
// the gate any more — logout still drops it defensively. The mechanism stays
// for the transition and goes away with the cookie. A never-set gate is null
// and request() skips the await outright, so it delays nothing (covered by
// the gate tests in __tests__/apiClient.test.js).
let authBootstrap: Promise<void> | null = null;

// Endpoints that establish or tear down the session. They must never wait on
// the gate: /auth/login *is* the gate (waiting would deadlock), and logout has
// to work while it is still open. The same endpoints are also the ones that
// stay cookie-only — they never carry the Supabase Bearer (see the module
// comment above).
const BOOTSTRAP_EXEMPT_PATHS = [
  '/auth/login',
  '/auth/login-v2',
  '/auth/register',
  '/auth/register-v2',
  '/auth/refresh',
  '/auth/logout',
];

/**
 * Make subsequent API calls wait for an in-flight login.
 *
 * Pass null to drop the gate (logout). The promise's rejection is swallowed:
 * callers only need to know the attempt has settled, and if it failed then a
 * 401 is the honest answer for whatever they were doing — blocking every
 * request forever is not.
 */
export function setAuthBootstrap(promise: Promise<unknown> | null): void {
  if (!promise) {
    authBootstrap = null;
    return;
  }
  const gate = promise.then(
    () => undefined,
    () => undefined,
  );
  authBootstrap = gate;
  // Once settled the gate guards nothing, so clear it instead of making every
  // later request await a resolved promise. Guarded against a newer login
  // having replaced it in the meantime.
  void gate.then(() => {
    if (authBootstrap === gate) authBootstrap = null;
  });
}

/** Response type compatible with the previous axios-based API */
export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  headers: Headers;
}

/** Error type thrown by the API client on non-2xx responses */
export class ApiError extends Error {
  response?: { status: number; data: unknown };

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.response = { status, data };
  }
}

interface RequestOptions {
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  responseType?: 'blob' | 'json';
}

// CSRF auto-recovery: when a state-changing request hits a 403 because
// our cached token desynced from the cookie (stale tab, post-deploy
// cache, etc.), call /auth/refresh once to re-issue both, then retry
// the original request. /auth/refresh's response body re-populates the
// cache via the same capture logic in request() below. The in-flight
// promise dedupes concurrent recoveries so parallel POSTs don't all
// stampede the refresh endpoint.
let csrfRefreshInFlight: Promise<void> | null = null;

function refreshCsrfTokenOnce(): Promise<void> {
  if (csrfRefreshInFlight) return csrfRefreshInFlight;
  csrfRefreshInFlight = (async () => {
    try {
      await request('POST', '/auth/refresh', undefined, {}, true);
    } finally {
      csrfRefreshInFlight = null;
    }
  })();
  return csrfRefreshInFlight;
}

/**
 * The Supabase access token to send as Bearer, or null when there is none.
 *
 * getSession() is an in-memory read (supabase-js caches the session and
 * refreshes it on its own), so this is cheap enough for every request. Any
 * failure — storage unavailable, a broken session record — degrades to
 * "no Bearer": the request then goes out cookie-only, which is exactly the
 * pre-migration behaviour, rather than failing before it is sent.
 */
async function getSupabaseAccessToken(): Promise<string | null> {
  if (!supabase) return null;
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return typeof token === 'string' && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

function hasAuthorizationHeader(headers: Record<string, string>): boolean {
  return Object.keys(headers).some((key) => key.toLowerCase() === 'authorization');
}

// Retry bookkeeping. `_isRetry` marks a request that request() re-issued
// itself — after a CSRF refresh, or cookie-only after a Bearer 401 — and no
// further recovery is attempted on it, so the two recoveries can add at most
// one extra request between them and can never chain into a loop.
// `_withoutBearer` is what the 401 fallback sets: send this attempt on the
// cookie alone even though a Supabase session exists.
async function request<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
  _isRetry: boolean = false,
  _withoutBearer: boolean = false,
): Promise<ApiResponse<T>> {
  // Wait for an in-flight login to produce the cookie + CSRF token this
  // request needs (see setAuthBootstrap above).
  if (authBootstrap && !BOOTSTRAP_EXEMPT_PATHS.includes(path)) {
    await authBootstrap;
  }

  // Build URL with query params
  let url = `${API_BASE_URL}${path}`;
  if (options.params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(options.params)) {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    }
    const qs = searchParams.toString();
    if (qs) url += `?${qs}`;
  }

  // Build headers
  const headers: Record<string, string> = {
    ...options.headers,
  };

  // Set Content-Type for JSON bodies (skip for FormData)
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  // Primary credential: the Supabase session as Bearer (see the module
  // comment). Skipped on the cookie-only bootstrap paths, on the cookie-only
  // retry, and when the caller supplied its own Authorization header. The
  // HttpOnly cookie always rides along via `credentials: 'include'` below,
  // so a request without a Bearer is the pre-migration cookie request.
  let bearerAttached = false;
  if (
    !_withoutBearer &&
    !BOOTSTRAP_EXEMPT_PATHS.includes(path) &&
    !hasAuthorizationHeader(headers)
  ) {
    const accessToken = await getSupabaseAccessToken();
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      bearerAttached = true;
    }
  }

  // Attach CSRF token for state-changing requests. Sent with the Bearer too:
  // the backend ignores it there, and the cookie path needs it.
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }

  const res = await fetch(url, {
    method: method.toUpperCase(),
    headers,
    body: body !== undefined && body !== null
      ? isFormData ? body as BodyInit : JSON.stringify(body)
      : undefined,
    credentials: 'include', // Send cookies with cross-origin requests
    signal: options.signal,
  });

  // Handle non-2xx responses
  if (!res.ok) {
    // Transition safety (see the module comment): a backend that predates
    // Bearer support answers the Supabase token with 401. Re-issue the
    // request once on the cookie alone; if that 401s too, the 401 is real
    // and surfaces below like any other. Never on a retry, so this cannot
    // chain with the CSRF recovery.
    if (res.status === 401 && bearerAttached && !_isRetry) {
      logger.warn(
        'Backend rejected the Supabase session token; retrying with the cookie only',
      );
      return request<T>(method, path, body, options, true, true);
    }

    // Beyond that, 401 responses are handled at the store level (auth state
    // reset). Nothing to clean up client-side — the cookie is HttpOnly and
    // the backend clears it on logout; a stale cookie just keeps failing.

    let errorData: unknown;
    try {
      errorData = await res.json();
    } catch {
      errorData = await res.text().catch(() => null);
    }

    // Stale-CSRF auto-recovery (see refreshCsrfTokenOnce above). Only
    // fire on the first attempt of non-/auth/ POSTs whose 403 detail
    // matches the backend's CSRF rejection — this guards against loops
    // and avoids hijacking legitimate authorization 403s.
    const csrfFailed =
      res.status === 403 &&
      typeof errorData === 'object' &&
      errorData !== null &&
      (errorData as { detail?: unknown }).detail === 'CSRF token validation failed';
    if (csrfFailed && !_isRetry && !path.startsWith('/auth/')) {
      try {
        await refreshCsrfTokenOnce();
        return request<T>(method, path, body, options, true);
      } catch {
        /* refresh failed — fall through to the original 403 below. */
      }
    }

    throw new ApiError(
      `Request failed with status ${res.status}`,
      res.status,
      errorData,
    );
  }

  // Parse response
  let data: T;
  if (options.responseType === 'blob') {
    data = await res.blob() as unknown as T;
  } else {
    const text = await res.text();
    data = text ? JSON.parse(text) : (null as unknown as T);
  }

  // Capture rotating CSRF token from auth-bootstrap responses so that
  // subsequent state-changing requests can replay it as X-CSRF-Token.
  // Cross-origin browsers can't read the csrf_token cookie, so the
  // response body is the only path the SPA has into this value.
  if (data && typeof data === 'object' && 'csrf_token' in data) {
    const token = (data as { csrf_token?: unknown }).csrf_token;
    if (typeof token === 'string' && token.length > 0) {
      setCsrfToken(token);
    }
  }

  return { data, status: res.status, headers: res.headers };
}

/** Fetch-based API client with the same method signatures as the former axios instance */
const api = {
  get: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>('GET', path, undefined, options),
  post: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
  put: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  patch: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),
  delete: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, undefined, options),
};

export default api;
